import {
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  ThemeProvider,
  createTheme,
  useTheme,
} from '@mui/material';
import React, { FC, ReactNode, useMemo } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import {
  ModalCloseWrapper,
  ModalMainWrapper,
  ModalTopWrapper,
  TDCDialog,
} from './constants';
import { theme2 } from '../../theme/theme';
import { TOKENS } from '../../theme/tokens';

interface ReplacementButtonOptions {
  label?: string;
  onClick: () => void;
}

interface Props {
  show: boolean;
  title: string;
  description?: string;
  children?: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  confirmButton?(options: ReplacementButtonOptions): ReactNode;
  cancelLabel?: string;
  onCancel: () => void;
  cancelButton?(options: ReplacementButtonOptions): ReactNode;
  confirmBtnColor?: string;
}

export const ConfirmationDialog: FC<Props> = ({
  show,
  title = 'Confirm',
  description,
  children,
  confirmLabel = 'Confirm',
  onConfirm,
  confirmButton,
  cancelLabel = 'Cancel',
  onCancel,
  cancelButton,
  confirmBtnColor = 'primary',
}) => {
  // theme2 carries this dialog's custom Button color variants (grey/green/
  // red), so it can't simply be dropped — instead, follow the ambient mode:
  // in dark, overlay theme2 with the dark surface/text tokens so the dialog
  // matches the rest of the app while keeping its button styling.
  const ambient = useTheme();
  const dialogTheme = useMemo(() => {
    if (ambient.palette.mode !== 'dark') return theme2;
    const t = TOKENS.dark;
    // TWO things are required here, and each is easy to get wrong:
    //
    // 1. palette.mode must live in the FIRST argument. createTheme runs
    //    createPalette on arg 1 only and deep-merges the rest, so a mode passed
    //    in a second argument never regenerates the mode-derived values.
    //
    // 2. theme2.palette's own `action` must be DROPPED. theme2 is an
    //    already-built LIGHT theme, so its palette carries a fully resolved
    //    action = rgba(0,0,0,…). createPalette deep-merges that over the dark
    //    defaults it just generated, and it wins — leaving a disabled Confirm
    //    label at rgba(0,0,0,.26) on #18212F (~1.05:1, invisible) and black
    //    hover/focus ripples on every dialog button. Omitting the key lets the
    //    dark set through; everything else in theme2.palette (the custom
    //    grey/green/red Button variants) is preserved by the rest spread.
    const { action: _lightAction, ...restPalette } = theme2.palette;
    return createTheme({
      ...theme2,
      palette: {
        ...restPalette,
        mode: 'dark' as const,
        background: { default: t.page, paper: t.surface },
        text: { primary: t.text, secondary: t.text2, disabled: t.muted },
        divider: t.border,
      },
      components: {
        ...theme2.components,
        MuiPaper: { styleOverrides: { root: { backgroundImage: 'none' } } },
      },
    });
  }, [ambient.palette.mode]);

  const defaultCancelButton = ({
    label,
    onClick,
  }: ReplacementButtonOptions) => (
    <Button color="grey" onClick={onClick}>
      {label}
    </Button>
  );
  const defaultConfirmButton = ({
    label,
    onClick,
  }: ReplacementButtonOptions) => (
    <Button color={confirmBtnColor as any} onClick={onClick}>
      {label}
    </Button>
  );

  return (
    <ThemeProvider theme={dialogTheme}>
      <TDCDialog
        open={show}
        onClose={onCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <ModalTopWrapper>
          <ModalMainWrapper>
            <DialogTitle id="alert-dialog-title" component="h6" fontFamily='CircularStdBlack'>
              {title}
            </DialogTitle>
            <DialogContent>
              {description && (
                <DialogContentText id="alert-dialog-description">
                  {description}
                </DialogContentText>
              )}
              {children}
            </DialogContent>
          </ModalMainWrapper>
          <ModalCloseWrapper>
            <CloseIcon onClick={onCancel} className="cursor-pointer" />
          </ModalCloseWrapper>
        </ModalTopWrapper>
        <DialogActions>
          {cancelButton
            ? cancelButton({ label: cancelLabel, onClick: onCancel })
            : defaultCancelButton({ label: cancelLabel, onClick: onCancel })}
          {confirmButton
            ? confirmButton({ label: confirmLabel, onClick: onConfirm })
            : defaultConfirmButton({ label: confirmLabel, onClick: onConfirm })}
        </DialogActions>
      </TDCDialog>
    </ThemeProvider>
  );
};
